'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useEscapeKey } from '../../hooks/useEscapeKey'

import { Box } from '@chakra-ui/react'

import type { ColorScheme } from '../../types'
import { Tooltip } from '../Tooltip'
import type { TooltipPlacement, TooltipVariant } from '../Tooltip'
import { getMenuStyles } from './ActionMenu.styles'

export type ActionMenuItem = {
  label: string
  icon?: React.ReactNode
  onClick: () => void
  disabled?: boolean
  color?: string
  /** Renders the row in a selected/active state (highlight + emphasis). */
  selected?: boolean
  /**
   * When set, hovering the row shows a Tooltip with this text. Most valuable
   * on a `disabled` row — the tooltip is then the only thing explaining why
   * the action is unavailable.
   */
  tooltipLabel?: string
  /**
   * Fired when this row's tooltip becomes visible, so callers can record that
   * the explanation actually reached the user.
   */
  onTooltipShow?: () => void
  /**
   * Stable React key. Falls back to `label`, so pass this whenever labels are
   * display strings that aren't guaranteed unique.
   */
  key?: string
  'data-testid'?: string
}

export type ActionMenuProps = {
  items: ActionMenuItem[]
  colorScheme?: ColorScheme
  /** Anchor the menu to the right edge of the trigger instead of the left. */
  anchorRight?: boolean
  /** Override the z-index of the dropdown menu. Defaults to 10. */
  zIndex?: number
  /** Caps the menu height; when set, the item list scrolls beyond it. */
  maxHeight?: number | string
  /** Font size for the menu items. Defaults to 12px. */
  fontSize?: number | string
  /**
   * Minimum width of the dropdown surface. Defaults to 214px — the Design
   * Library width, which suits the short labels of a row-level kebab menu.
   * Raise it for menus whose entries carry longer copy.
   */
  minWidth?: number | string
  /**
   * Vertical and horizontal padding of each row, which is what sets the row
   * height. Defaults to 10px.
   */
  itemPadding?: number | string
  /**
   * Line box of the row label. Defaults to 16px, which suits the default 12px
   * `fontSize`; raise it alongside `fontSize` or the text crowds its box.
   */
  itemLineHeight?: number | string
  /**
   * Placement for item tooltips. Defaults to `bottom`, so the explanation
   * appears directly under the row it belongs to.
   */
  tooltipPlacement?: TooltipPlacement
  /**
   * Variant for item tooltips. Defaults to `label` — the same variant the
   * action-bar buttons use, so a menu-row tooltip and a button tooltip in the
   * same drawer read identically. `label` is single-line and uppercase, so a
   * long `tooltipLabel` makes for a wide tooltip rather than a wrapped one.
   */
  tooltipVariant?: TooltipVariant
  /**
   * Render prop for the trigger element. Receives `isOpen` so the trigger
   * can reflect the menu state (e.g. selected highlight).
   */
  trigger: (props: { onClick: () => void; isOpen: boolean }) => React.ReactNode
}

export const ActionMenu = ({
  items,
  colorScheme = 'dark',
  anchorRight = false,
  zIndex,
  maxHeight,
  fontSize,
  minWidth,
  itemPadding,
  itemLineHeight,
  tooltipPlacement = 'bottom',
  tooltipVariant = 'label',
  trigger,
}: ActionMenuProps) => {
  const [isOpen, setIsOpen] = useState(false)
  const [hoveredTooltip, setHoveredTooltip] = useState<{
    anchorEl: HTMLElement
    label: string
  } | null>(null)
  const wrapperRef = useRef<HTMLDivElement>(null)

  const handleClick = () => setIsOpen((prev) => !prev)

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return
    const handleClickOutside = (e: MouseEvent) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isOpen])

  // Close on Escape key, without letting a surrounding drawer/modal close too
  const handleEscape = useCallback(() => setIsOpen(false), [])
  useEscapeKey(handleEscape, isOpen)

  // A closing menu unmounts the hovered row without firing mouseleave, which
  // would otherwise leave its tooltip anchored to a detached node.
  useEffect(() => {
    if (!isOpen) setHoveredTooltip(null)
  }, [isOpen])

  // Disabling the trigger only blocks a NEW open — an already-open menu keeps
  // its own isOpen state, so if items empty out mid-session (e.g. a facility/
  // floor switch clears the list while the menu is still open) it would
  // otherwise keep rendering an empty panel until the user clicks away.
  useEffect(() => {
    if (isOpen && items.length === 0) setIsOpen(false)
  }, [isOpen, items.length])

  const styles = getMenuStyles(colorScheme)

  return (
    <Box display='inline-block' position='relative' ref={wrapperRef}>
      {trigger({ onClick: handleClick, isOpen })}
      {isOpen && (
        <Box
          {...styles.menu}
          {...styles.menuScroll(maxHeight)}
          left={anchorRight ? undefined : '0'}
          minWidth={minWidth ?? styles.menu.minWidth}
          mt='4px'
          position='absolute'
          right={anchorRight ? '0' : undefined}
          role='menu'
          top='100%'
          zIndex={zIndex ?? styles.menu.zIndex}
        >
          {items.map((item) => (
            <Box
              {...styles.item(
                !!item.disabled,
                !!item.selected,
                fontSize,
                itemPadding,
                itemLineHeight
              )}
              color={
                item.color ??
                styles.item(!!item.disabled, !!item.selected, fontSize).color
              }
              data-testid={item['data-testid']}
              key={item.key ?? item.label}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation()
                if (!item.disabled) {
                  item.onClick()
                  setIsOpen(false)
                }
              }}
              onMouseEnter={(e: React.MouseEvent<HTMLElement>) => {
                if (!item.tooltipLabel) return
                setHoveredTooltip({
                  anchorEl: e.currentTarget,
                  label: item.tooltipLabel,
                })
                item.onTooltipShow?.()
              }}
              // Cleared unconditionally: an item can lose its tooltipLabel
              // while hovered (a workflow finishes loading, say), and a
              // guarded clear would leave the tooltip stuck on screen.
              onMouseLeave={() => setHoveredTooltip(null)}
              role='menuitem'
            >
              {item.icon && (
                <Box as='span' flexShrink={0}>
                  {item.icon}
                </Box>
              )}
              {item.label}
            </Box>
          ))}
        </Box>
      )}
      {hoveredTooltip && (
        <Tooltip
          anchorEl={hoveredTooltip.anchorEl}
          colorScheme={colorScheme}
          label={hoveredTooltip.label}
          placement={tooltipPlacement}
          variant={tooltipVariant}
        />
      )}
    </Box>
  )
}
